import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ClipboardCheck, Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VisitareAlterarPasswordPage() {
  const navigate = useNavigate();
  const [passwordAtual, setPasswordAtual] = useState('');
  const [passwordNova, setPasswordNova] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!passwordAtual || !passwordNova || !passwordConfirm) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    if (passwordNova !== passwordConfirm) {
      toast.error('As passwords não coincidem');
      return;
    }
    
    if (passwordNova.length < 6) {
      toast.error('A nova password deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('visitare_token');

    try {
      const formData = new FormData();
      formData.append('password_atual', passwordAtual);
      formData.append('password_nova', passwordNova);

      const response = await fetch(`${API_URL}/api/visitare/alterar-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao alterar password');
      }

      toast.success('Password alterada com sucesso!');
      
      // Update local user data
      const userData = JSON.parse(localStorage.getItem('visitare_user') || '{}');
      userData.must_change_password = false;
      localStorage.setItem('visitare_user', JSON.stringify(userData));
      
      navigate('/visitare/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0066CC] rounded-2xl mb-4">
            <ClipboardCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">VISITARE</h1>
          <p className="text-slate-400 mt-2">Portal de Visitadores</p>
        </div>

        <Card className="border-0 shadow-2xl">
          <CardHeader>
            <CardTitle>Alterar Password</CardTitle>
            <CardDescription>
              Por segurança, deve alterar a password temporária antes de continuar.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="passwordAtual">Password Atual</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="passwordAtual"
                    type={showPasswords ? 'text' : 'password'}
                    value={passwordAtual}
                    onChange={e => setPasswordAtual(e.target.value)}
                    placeholder="Password temporária"
                    className="pl-10"
                    data-testid="password-atual-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="passwordNova">Nova Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="passwordNova"
                    type={showPasswords ? 'text' : 'password'}
                    value={passwordNova}
                    onChange={e => setPasswordNova(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="pl-10"
                    data-testid="password-nova-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="passwordConfirm">Confirmar Nova Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="passwordConfirm"
                    type={showPasswords ? 'text' : 'password'}
                    value={passwordConfirm}
                    onChange={e => setPasswordConfirm(e.target.value)}
                    placeholder="Repita a nova password"
                    className={`pl-10 ${passwordConfirm && passwordNova === passwordConfirm ? 'border-green-500' : ''}`}
                    data-testid="password-confirm-input"
                  />
                  {passwordConfirm && passwordNova === passwordConfirm && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showPasswords"
                  checked={showPasswords}
                  onChange={e => setShowPasswords(e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="showPasswords" className="text-sm cursor-pointer">
                  Mostrar passwords
                </Label>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#0066CC] hover:bg-[#0052a3]" 
                disabled={loading}
                data-testid="alterar-password-btn"
              >
                {loading ? 'A alterar...' : 'Alterar Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-8">
          © {new Date().getFullYear()} ENTRAJUDATA - Portal Visitare
        </p>
      </div>
    </div>
  );
}
